import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SourceDocumentBusinessLogic from '../../businessLogic/SourceDocumentBusinessLogic';
import { IFileGuiModel, ISourceDocumentGuiModel } from '../../model/ISourceDocumentGuiModel';
import { Typography, Select, MenuItem, CircularProgress, Grid, Paper, FormControl, InputLabel, Stack } from '@mui/material';
import { getTokenFromLocalStorage } from "../../App";
import { RedirectToLogin } from '../../utils';
import FileViewer from '../voucherSearch/components/FileViewer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';

const SourceDocumentWeblink = () => {
    const { organizationId, documentId } = useParams();
    const [sourceDocument, setSourceDocument] = useState<ISourceDocumentGuiModel | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<IFileGuiModel | null>(null);
    const [fileViewerOpen, setFileViewerOpen] = useState<boolean>(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const token = getTokenFromLocalStorage();
        if (!token) {
            RedirectToLogin(window.location.pathname);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchSourceDocument = async () => {
            setLoading(true);
            const businessLogic = new SourceDocumentBusinessLogic();
            if (!organizationId || !documentId) {
                const errorMsg = t("sourceDocumentWeblink.orgIdAndDocIdNotProvided");
                setError(errorMsg);
                toast.error(errorMsg);
                setLoading(false);
                return;
            }

            try {
                const result = await businessLogic.getSourceDocument(organizationId, Number(documentId), false);
                if (result.errors.length > 0) {
                    throw new Error(result.errors.join(", "));
                }
                if (isMounted) {
                    setSourceDocument(result.value);
                }
            } catch (err: unknown) {
                if (isMounted) {
                    if (err instanceof Error) {
                        setError(err.message);
                        toast.error(err.message);
                    } else {
                        const unknownErrorMsg = t("sourceDocumentWeblink.unknownError");
                        setError(unknownErrorMsg);
                        toast.error(unknownErrorMsg);
                    }
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchSourceDocument();

        return () => {
            isMounted = false;
        };
    }, [organizationId, documentId, navigate, t]);

    useEffect(() => {
        const primaryFile = sourceDocument?.files?.find(file => file.isPrimary) || null;
        if (primaryFile) {
            setSelectedFile(primaryFile);
            setFileViewerOpen(true);
        }
    }, [sourceDocument]);

    const handleFileSelection = (file: IFileGuiModel) => {
        setSelectedFile(file);
        setFileViewerOpen(true);
    };

    const closeFileViewer = () => {
        setFileViewerOpen(false);
        setSelectedFile(null);
    };

    if (loading) {
        return (
            <Grid container sx={{ height: '100vh', width: '100%', position: 'relative' }} justifyContent="center" alignItems="flex-start">
                <Grid item xs={12} textAlign="center" sx={{ mt: 2 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>{t("basic.loading")}</Typography>
                </Grid>
            </Grid>
        );
    }

    if (error) return <Typography color="error">Error: {error}</Typography>;

    if (!sourceDocument || !sourceDocument.files || sourceDocument.files.length === 0) {
        return (
            <Grid item component={Paper} elevation={8} xs={12}>
                <Typography sx={{ height: "100%", width: "100%" }}>{t("basic.documentNoFiles")}</Typography>
            </Grid>
        );
    }

    return (
        <Grid container component={Paper} elevation={8} sx={{ height: '100vh' }}>
            <Grid item xs={12} sx={{ height: '4rem' }}>
                <Stack direction="row" margin={2} justifyContent="space-between">
                    <Stack direction="row" spacing={2}>
                        <FormControl color="warning" size="small" sx={{ minWidth: "11rem", maxWidth: "16rem" }}>
                            <InputLabel id="select-doc">{t("search.documentChoice")}</InputLabel>
                            <Select
                                color="warning"
                                labelId="select-doc-label"
                                id="select-doc"
                                value={selectedFile?.id.toString() || ''}
                                label={t("search.documentChoice")}
                                onChange={(e) => {
                                    const fileId = parseInt(e.target.value, 10);
                                    const selected = sourceDocument.files?.find(file => file.id === fileId) || null;
                                    if (selected) handleFileSelection(selected);
                                }}
                            >
                                {sourceDocument.files.map(file => (
                                    <MenuItem key={file.id} value={file.id}>
                                        {file.fileName || "Unnamed File"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </Grid>
            <Grid item xs={12} sx={{ height: `calc(100vh - 4rem - ${theme.spacing(1)})`, width: '100%', position: 'relative' }}>
                {fileViewerOpen && selectedFile && (
                    <FileViewer
                        showFile={fileViewerOpen}
                        showFileToggle={closeFileViewer}
                        file={selectedFile}
                        sourceDocumentId={sourceDocument.id}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default SourceDocumentWeblink;